/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function stringifyOperations(operations) {
    var stackoperations = Array.isArray(operations)
        ? operations
        : [operations];
    return stackoperations
        .map(function (operation) {
        var name = operation.name;
        var options = Object.keys(operation.options || {})
            .map(function (k) {
            return "".concat(k, "-").concat(operation.options && operation.options[k]
                ? operation.options[k]
                : '__undefined__');
        })
            .join('-');
        if (!options) {
            return name;
        }
        return "".concat(name, "-").concat(options);
    })
        .join('--');
}
function stringifyStackOptions(options) {
    var allOptions = [];
    Object.keys(options).forEach(function (key) {
        allOptions.push("".concat(key, "-").concat(options[key]));
    });
    return allOptions.join('-');
}
var stringifyBool = function (value) {
    if (value === false) {
        return 'false';
    }
    if (value === true) {
        return 'true';
    }
    return value;
};

// currently only gets stack variables
var getStackComponents = function (stack) {
    // split by slashes
    // TODO: if we parse operations and options, only the stuff before the first / is a dynamic operation
    // otherwise it's just new options for that operation
    // but we don't parse operations yet, here.
    var slashSplits = stack.split('/');
    var variables = {};
    for (var i = 0; i < slashSplits.length; i++) {
        var slashSplit = slashSplits[i];
        // then by --, both are valid seperators here
        var dashSplits = slashSplit.split('--');
        for (var j = 0; j < dashSplits.length; j++) {
            var dashSplit = dashSplits[j];
            // and get the object for the variables (or other "parts", later)
            var _a = dashSplit.split('-'), operationName = _a[0], options = _a.slice(1);
            if (operationName === 'v' || operationName === 'variables') {
                var name_1 = '';
                // we have a match, set it to empty
                dashSplits[j] = '';
                for (var k = 0; k < options.length; k++) {
                    if (k % 2 === 0) {
                        name_1 = options[k];
                    }
                    else {
                        variables[name_1] = options[k];
                    }
                }
            }
        }
        // put them back together
        slashSplits[i] = dashSplits.filter(function (dashSplit) { return dashSplit !== ''; }).join('--');
    }
    var stackString = slashSplits
        .filter(function (slashSplit) { return slashSplit !== ''; })
        .join('/');
    return { variables: variables, stackString: stackString };
};
var getUrlComponents = function (urlObject) {
    var _a;
    var stackPattern = '(?<stack>.*([^-]|--)|-*)';
    var hashPattern = '(?<hash>[0-9a-f]{6,40})';
    var filenamePattern = '(?<filename>[^\\/^.]+)';
    var formatPattern = '(?<format>.{2,4})';
    var pathPattern = '(?<hash>-.+-)';
    var regExes = [
        new RegExp("/".concat(stackPattern, "/").concat(hashPattern, "/").concat(filenamePattern, ".").concat(formatPattern)),
        new RegExp("/".concat(stackPattern, "/").concat(hashPattern, ".").concat(formatPattern)),
        new RegExp("/".concat(stackPattern, "/").concat(pathPattern, "/").concat(filenamePattern, ".").concat(formatPattern)),
        new RegExp("/".concat(stackPattern, "/").concat(pathPattern, ".").concat(formatPattern)),
    ];
    var path = urlObject.pathname;
    var matches = null;
    for (var i = 0; i < regExes.length; i++) {
        matches = path.match(regExes[i]);
        if (matches) {
            break;
        }
    }
    if (matches !== null && ((_a = matches.groups) === null || _a === void 0 ? void 0 : _a['stack'])) {
        return {
            stack: matches.groups['stack'],
            hash: matches.groups['hash'],
            format: matches.groups['format'],
            filename: matches.groups['filename'],
        };
    }
    return false;
};
var getStackVariables = function (urlComponents, urlObject, stackComponents, variables) {
    var variablesFromPath = stackComponents.variables;
    var vQuery = urlObject.searchParams.get('v');
    var vQueryParsed = vQuery !== null ? JSON.parse(vQuery) : {};
    return Object.assign(variablesFromPath, vQueryParsed, variables);
};
/**
 * ### Render
 *
 * @module render
 */
var getUrl = function (organization, hash, format, stack, options, renderHost) {
    if (renderHost === void 0) { renderHost = 'https://{organization}.rokka.io'; }
    var host = renderHost.replace('{organization}', organization);
    var mixedParam = Array.isArray(stack)
        ? "dynamic/".concat(stringifyOperations(stack)) // array of operations
        : stack; // stack name
    var stackString = mixedParam || 'dynamic/noop';
    if (options === null || options === void 0 ? void 0 : options.stackoptions) {
        var stackoptions = stringifyStackOptions(options === null || options === void 0 ? void 0 : options.stackoptions);
        if (stackoptions) {
            stackString = "".concat(stackString, "/o-").concat(stackoptions);
        }
    }
    if (options === null || options === void 0 ? void 0 : options.filename) {
        hash = "".concat(hash, "/").concat(options.filename);
    }
    var url = "".concat(host, "/").concat(stackString, "/").concat(hash, ".").concat(format);
    if ((options === null || options === void 0 ? void 0 : options.variables) && Object.keys(options.variables).length > 0) {
        return addStackVariables(url, options.variables, options.removeSafeUrlFromQuery || false);
    }
    return url;
};
/**
 * Get URL for rendering an image from a rokka render URL.
 *
 * ```js
 * rokka.render.getUrl('https://myorg.rokka.io/dynamic/c421f4e8cefe0fd3aab22832f51e85bacda0a47a.png', 'mystack')
 * ```
 *
 * @param  {string}                      rokkaUrl    rokka render URL
 * @param  {string|array}                stack       stack name or an array of stack operation objects
 * @param  {{filename:string|undefined, stackoptions: StackOptions|undefined, format: string|undefined, variables: Variables|undefined, clearVariables:boolean|undefined, removeSafeUrlFromQuery: boolean|undefined}} options     Optional. filename: Adds or changes the filename to the URL, stackoptions: Adds stackoptions to the URL, format: Changes the format
 * @return {string}
 */
var getUrlFromUrl = function (rokkaUrl, stack, options, renderHost) {
    if (options === void 0) { options = {}; }
    if (renderHost === void 0) { renderHost = 'https://{organization}.rokka.io'; }
    var url = new URL(rokkaUrl);
    var components = getUrlComponents(url);
    if (!components) {
        return rokkaUrl;
    }
    var resolvedOptions = __assign({ stackoptions: {}, filename: components.filename, format: components.format, removeSafeUrlFromQuery: false, clearVariables: true, variables: {} }, options);
    var variables = resolvedOptions.variables;
    if (!resolvedOptions.clearVariables) {
        var stackComponents = getStackComponents(components.stack);
        variables = getStackVariables(components, url, stackComponents, resolvedOptions.variables);
    }
    var renderHostUrl = new URL(renderHost);
    var replaceHost = decodeURIComponent(renderHostUrl.host).replace('{organization}', '');
    return getUrl(url.hostname.replace(replaceHost, ''), components.hash, resolvedOptions.format, stack, {
        filename: resolvedOptions.filename,
        stackoptions: resolvedOptions.stackoptions,
        variables: variables,
        removeSafeUrlFromQuery: resolvedOptions.removeSafeUrlFromQuery,
    }, renderHost);
};
var addStackVariables = function (url, variables, removeSafeUrlFromQuery) {
    if (removeSafeUrlFromQuery === void 0) { removeSafeUrlFromQuery = false; }
    var urlObject = new URL(url);
    var urlComponents = getUrlComponents(urlObject);
    if (!urlComponents) {
        return url;
    }
    var stackComponents = getStackComponents(urlComponents.stack);
    var returnVariables = getStackVariables(urlComponents, urlObject, stackComponents, variables);
    // put variables into url string or v parameter, depending on characters in it
    if (Object.keys(returnVariables).length > 0) {
        var jsonVariables = {};
        var urlVariables = '';
        for (var name_2 in returnVariables) {
            var value = returnVariables[name_2];
            if (value || value === false) {
                var valueAsString = value.toString();
                // if there's a special var in the value, put it into the v query parameter
                if (valueAsString.length > 20 ||
                    valueAsString.match(/[$/\\\-#%&?; ]/)) {
                    jsonVariables[name_2] = stringifyBool(value);
                }
                else {
                    urlVariables += '-' + name_2 + '-' + stringifyBool(value);
                }
            }
        }
        if (urlVariables !== '') {
            stackComponents.stackString += '/v' + urlVariables;
        }
        if (Object.keys(jsonVariables).length > 0) {
            urlObject.searchParams.set('v', JSON.stringify(jsonVariables));
        }
        else {
            urlObject.searchParams.delete('v');
        }
    }
    urlObject.pathname =
        stackComponents.stackString +
            '/' +
            urlComponents.hash +
            (urlComponents.filename ? '/' + urlComponents.filename : '') +
            '.' +
            urlComponents.format;
    // remove url safe characters on demand for "nicer" urls in demos and such
    if (removeSafeUrlFromQuery) {
        var query = urlObject.search;
        urlObject.search = '';
        return (urlObject.toString() +
            query
                .replace(/%22/g, '"')
                .replace(/%20/g, ' ')
                .replace(/%2C/g, ',')
                .replace(/%7B/g, '{')
                .replace(/%7D/g, '}')
                .replace(/%3A/g, ':'));
    }
    return urlObject.toString();
};

export { addStackVariables, getStackVariables, getUrl, getUrlFromUrl };
